import {ValidEmail} from 'Core/models/Email';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {applicativeValidation, lift} from 'Shared/utilities/ToValidation';
import {sequenceT} from 'fp-ts/lib/Apply';
import {Either, getOrElse, left, map as mapEither, mapLeft, right} from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';

const emailsMustMatch = (emailTuple: [string, string]): Either<string, [string, string]> => {
  const [email, confirmation] = emailTuple;
  return email === confirmation ? right([email, confirmation]) : left('Emails must match');
};

const emailMustBeValid = (emailTuple: [string, string]): Either<string, [string, string]> => {
  const [email, confirmation] = emailTuple;
  const validatedEmail = ValidEmail.create(email);

  return validatedEmail instanceof ValidEmail ? right([email, confirmation]) : left('Must be a valid email.');
};

const emailsMustMatchV = lift(emailsMustMatch);
const emailMustBeValidV = lift(emailMustBeValid);

const validateNewEmail = (email: string, confirmation: string) =>
  pipe(
    sequenceT(applicativeValidation)(emailsMustMatchV([email, confirmation]), emailMustBeValidV([email, confirmation])),
    mapEither(() => [email, confirmation])
  );

export class UnvalidatedNewEmail {
  public readonly key = 'UnvalidatedNewEmail';

  constructor(public readonly address: string, public readonly confirmation: string) {}
}

export class InvalidNewEmail {
  public static create(email: string, confirmation: string, errors: string[]) {
    return email === '' ? new UnvalidatedNewEmail('', '') : new InvalidNewEmail(email, confirmation, errors);
  }
  public readonly key = 'InvalidNewEmail';

  private constructor(
    public readonly address: string,
    public readonly confirmation: string,
    public readonly errors: string[]
  ) {}
}

export class ValidNewEmail {
  public static create(email: string, confirmation: string): NewEmail {
    const emailEither = pipe(
      validateNewEmail(email, confirmation),
      mapLeft(errors => InvalidNewEmail.create(email, confirmation, errors)),
      mapEither(([validEmail, validConfirmation]) => new ValidNewEmail(validEmail, validConfirmation))
    );

    return pipe(
      emailEither,
      getOrElse(invalidEmail => invalidEmail as NewEmail)
    );
  }

  public readonly key = 'ValidNewEmail';
  private constructor(public readonly address: string, public readonly confirmation: string) {}
}

export type NewEmail = ValidNewEmail | InvalidNewEmail | UnvalidatedNewEmail;

export type EmailOptions<TReturn> = ToMatchOptions<NewEmail, 'key', TReturn>;

export const matchNewEmail = <TReturn>(email: NewEmail, options: EmailOptions<TReturn>): TReturn => {
  switch (email.key) {
    case 'InvalidNewEmail':
      return options.InvalidNewEmail(email);
    case 'UnvalidatedNewEmail':
      return options.UnvalidatedNewEmail(email);
    case 'ValidNewEmail':
      return options.ValidNewEmail(email);
  }
};
